/**
 * Purpose: Handles WebSocket events related to voice/video calls in the application
 *
 * How it works:
 * 1. Listens for call-related events from the WebSocket server
 * 2. Updates the global call state based on these events
 * 3. Manages active call tracking across different chat types (P2P and Group)
 * 4. Handles user participation status in calls
 *
 * Key features:
 * - Tracks active calls across the application
 * - Differentiates between group and P2P calls
 * - Manages user connection/disconnection states
 * - Handles meeting start/end events
 * - Processes participant join/leave events
 */

import { isUserAuthenticated } from "@/middleware/auth";
import {
  MeetingEventType,
  ParticipantEventType,
  RoomFullEventType,
  RoomData,
} from "./types";

import { useCallState } from "@/zustand/CallState";
import { getChatFromLocalDB } from "@/indexedDB/services/chats";
import { ChatEntity } from "@/indexedDB/types";
import { ChatType } from "@/server/types/chat";
import {
  showMeetingNotification,
  closeMeetingNotification,
} from "@/utils/appNotification";
import { isAppVisible } from "@/utils/visibilityState";
import { toast } from "@/hooks/useToast";
import i18n from "@/config/i18n";

/**
 * Adds a call ID to the active calls list if it doesn't already exist.
 * Returns the chat entity so callers can reuse it without a redundant DB fetch.
 *
 * @param roomId - The ID of the room/chat where the call is happening
 */
async function addCallId(roomId: string): Promise<ChatEntity | null> {
  const state = useCallState.getState();
  const exists = state.activeCallIds.includes(roomId);
  if (!exists) {
    state.setActiveCallIds([...state.activeCallIds, roomId]);
    return await setActiveCallFlag(roomId);
  }
  // Call already tracked — still return the chat entity so callers don't need a redundant DB fetch
  return (await getChatFromLocalDB(roomId)) ?? null;
}

/**
 * Sets the appropriate active call flag based on the chat type.
 * Returns the chat entity so callers can reuse it.
 *
 * @param roomId - The ID of the room/chat to check the type for
 */
async function setActiveCallFlag(roomId: string): Promise<ChatEntity | null> {
  const chat = await getChatFromLocalDB(roomId);
  if (!chat) {
    return null;
  }
  if (chat.isArchived) {
    return chat;
  }
  const chatType = chat.type;
  if (chatType === ChatType.GROUP) {
    useCallState.getState().setHasActiveCallInGroups(true);
  } else {
    useCallState.getState().setHasActiveCallInP2Ps(true);
  }
  return chat;
}

/**
 * Handles the event when a meeting/call has started
 *
 * @param data - Meeting event data containing room information
 */
export async function handleMeetingStarted(data: MeetingEventType) {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }
  // Extract the room ID from the room identifier
  const roomId = data.room.split("@")[0];

  // Capture joinedCall before any async work to narrow the race window
  const { joinedCall: initialJoinedCall } = useCallState.getState();

  const chat = await addCallId(roomId);

  // Skip notification if the user is already in this call (they started it).
  // Check both pre- and post-async state to mitigate race conditions.
  const { joinedCall } = useCallState.getState();
  if (initialJoinedCall === roomId || joinedCall === roomId) {
    return;
  }

  // Show native notification if app is in the background.
  if (!isAppVisible() && chat && !chat.isMuted && !chat.isArchived) {
    const chatType = chat.type === ChatType.GROUP ? "GROUP" : "P2P";
    void showMeetingNotification(chat.name, roomId, chatType);
  }
}

/**
 * Handles the event when a meeting/call has ended
 *
 * @param data - Meeting event data containing room information
 */
export async function handleMeetingEnded(data: MeetingEventType) {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }
  const roomId = data.room.split("@")[0];

  // Close any meeting notification for this room
  void closeMeetingNotification(roomId);

  useCallState.getState().removeFromActiveCalls(roomId);
  useCallState.getState().setCallEnding(false);

  // Update active call flags for any remaining calls
  useCallState.getState().activeCallIds.forEach((id) => setActiveCallFlag(id));

  // Update user connection state
  useCallState.getState().setUserDisconnected();
}

/**
 * Handles the event when a participant joins a call
 *
 * @param data - Participant event data containing room and username information
 */
export async function handleParticipantJoined(data: ParticipantEventType) {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  const roomId = data.room.split("@")[0];
  // Check if the current user is the one who joined. This must be set
  // synchronously BEFORE the await below: socket handlers in this codebase
  // are dispatched without being awaited, so a concurrent room-full
  // rejection for the same room can run while addCallId() is still pending.
  // handleRoomFull's "already joined" no-op gate checks joinedCall === roomId,
  // so joinedCall must already reflect this join before we yield here —
  // otherwise a valid join can be spuriously reset by that rejection.
  if (userFingerPrint.username === data.username) {
    useCallState.getState().setJoinedCall(roomId);
  }
  await addCallId(roomId);
}

/**
 * Handles the event when a participant leaves a call
 * Only processes the event if it's the current user who left
 *
 * @param data - Participant event data containing room and username information
 */
export async function handleParticipantLeft(data: ParticipantEventType) {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint || userFingerPrint?.username !== data.username) {
    return;
  }

  useCallState.getState().setCallEnding(false);
  useCallState.getState().setUserDisconnected();
}

/**
 * Handles the event emitted when the backend rejects a join attempt because
 * the room already hit its subscription participant limit.
 *
 * Only reacts if this rejection is for the local user's own in-flight join
 * attempt (same room, not yet confirmed as joined via participant-joined) —
 * a room-full event for a call the local user isn't trying to join is a no-op.
 *
 * @param data - Room-full event data containing room and username information
 */
export async function handleRoomFull(data: RoomFullEventType) {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint || userFingerPrint.username !== data.username) {
    return;
  }

  const roomId = data.room.split("@")[0];
  const { callInfo, joinedCall } = useCallState.getState();

  // Not the room the local user is currently trying to join, or already
  // confirmed joined (this rejection can't apply to them) — ignore.
  if (!callInfo || callInfo.currentCallId !== roomId || joinedCall === roomId) {
    return;
  }

  // Reset call state the same way handleCancelCall/setUserDisconnected do,
  // so the user isn't left stuck showing "joining...".
  useCallState.getState().setUserDisconnected();

  toast({
    title: i18n.t("videoCall.roomFullTitle"),
    description: i18n.t("videoCall.roomFullDescription"),
  });
}

/**
 * Handles the event that provides information about all active call rooms
 * Updates the global state with all active call IDs
 *
 * @param data - Array of room data objects containing chat IDs
 */
export async function getGetActiveRoomsCallback(data: RoomData[]) {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }
  if (data === null) {
    return;
  }
  // Extract chat IDs from room data. An empty array is a valid, authoritative
  // "no active rooms" answer — it must still replace activeCallIds so a
  // ghost call self-heals via periodic refresh even when it was the user's
  // only active call (see F1 in call-status-rca.md). Bailing out here used
  // to silently keep stale state in that exact case.
  const ids = data.map((e) => e.chat_id);
  useCallState.getState().setActiveCallIds(ids);
  if (ids.length === 0) {
    // Mirror removeFromActiveCalls' full-clear branch: no active rooms means
    // no active-call indicator anywhere.
    useCallState.getState().setHasActiveCallInGroups(false);
    useCallState.getState().setHasActiveCallInP2Ps(false);
    return;
  }
  // Recompute BOTH category flags from scratch based on the authoritative
  // refresh, rather than only OR-ing them to true (setActiveCallFlag's
  // per-id behavior, which is correct for the incremental
  // join/meeting-started paths but wrong here).
  //
  // Why this matters: a user can have an active P2P call AND an active group
  // call at once. If the P2P call ends via a missed event, an authoritative
  // refresh that comes back group-only must be able to flip
  // hasActiveCallInP2Ps back to false — otherwise the P2P ghost-call
  // indicator (e.g. NavigationMenu badge) never self-heals, even though
  // activeCallIds itself is correct. OR-ing to true can only ever add
  // categories, never remove a stale one.
  const chats = await Promise.all(ids.map((id) => getChatFromLocalDB(id)));
  const activeChats = chats.filter(
    (chat): chat is ChatEntity => !!chat && !chat.isArchived,
  );
  const hasActiveCallInGroups = activeChats.some(
    (chat) => chat.type === ChatType.GROUP,
  );
  const hasActiveCallInP2Ps = activeChats.some(
    (chat) => chat.type !== ChatType.GROUP,
  );
  useCallState.getState().setHasActiveCallInGroups(hasActiveCallInGroups);
  useCallState.getState().setHasActiveCallInP2Ps(hasActiveCallInP2Ps);
}
